import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AnySchema, boolean as yupBoolean, object as yupObject } from 'yup'
import {
  ComboboxGroupItem,
  ComboboxGroupProps,
  ComboboxItem,
  ComboboxProps,
} from '@node-space/ui-components'
import { FieldMaxLength } from 'constants/forms'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useIndustryOptions } from 'hooks/queries/Customers/useIndustryOptions'
import { useMonthlyVolumesQuery } from 'hooks/queries/Customers/useMonthlyVolumesQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { ErrorDetails } from 'types/types'
import { getCountryOptions } from 'utils/forms'
import { RADIO_OPTIONS } from '../helpers/constants'
import {
  BASE_CUSTOMER_OPTIONS,
  BUSINESS_CUSTOMER_LABEL_MAPPING,
  mapBusinessErrorKeyToFormKey,
  setFieldErrors,
} from '../helpers/form'
import {
  BusinessCustomerFormData,
  BusinessCustomerFormKey,
  RiskScore,
  UseCase,
} from '../helpers/types'

type BusinessCustomerFormSchema = Record<BusinessCustomerFormKey, AnySchema>

export const useBusinessCustomerForm = (
  defaultValues: BusinessCustomerFormData,
  fieldErrors?: ErrorDetails,
  enableRelianceBusinessFields?: boolean
) => {
  const { t } = useTranslation()
  const { getRequiredYupField, getYupField } = useYupFields()

  const {
    data: countriesData = [],
    isFetching: isFetchingCountries,
    isError: isErrorFetchingCountries,
  } = useCountriesQuery()

  const {
    industryOptions,
    isPending: isFetchingIndustries,
    error: isErrorFetchingIndustries,
  } = useIndustryOptions()

  const {
    data: monthlyVolumes,
    isFetching: isFetchingMonthlyVolumes,
    isError: isErrorFetchingMonthlyVolumes,
  } = useMonthlyVolumesQuery()

  const comboboxItems: Partial<Record<BusinessCustomerFormKey, ComboboxItem[]>> = {
    ...BASE_CUSTOMER_OPTIONS,
    countryOfIncorporation: getCountryOptions(countriesData),
    monthlyExpectedVolumes: monthlyVolumes?.map(data => ({
      leftText: data.name,
      value: data.reference,
    })),
  }

  const comboboxGroupItems: Partial<Record<BusinessCustomerFormKey, ComboboxGroupItem[]>> = {
    industry: industryOptions,
  }

  const isComboboxLoading: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isFetchingCountries,
    monthlyExpectedVolumes: isFetchingMonthlyVolumes,
    industry: isFetchingIndustries,
  }

  const isErrorLoadingOptions: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isErrorFetchingCountries,
    industry: isErrorFetchingIndustries,
    monthlyExpectedVolumes: isErrorFetchingMonthlyVolumes,
  }

  const optionErrorFields = Object.keys(isErrorLoadingOptions)?.filter(
    key => isErrorLoadingOptions?.[key]
  )

  const formKeys = (() => {
    const keys = Object.keys(defaultValues) as BusinessCustomerFormKey[]
    if (enableRelianceBusinessFields && !keys.includes('eddCompleted')) {
      keys.push('eddCompleted', 'riskScore')
    }
    return keys
  })()

  const getFieldSchema = (key: BusinessCustomerFormKey): AnySchema => {
    const label = t(
      BUSINESS_CUSTOMER_LABEL_MAPPING?.[key] ?? `embeddedCustomers.businessForm.${key}`
    )
    const fieldSchema = getRequiredYupField({ label })
    switch (key) {
      case 'businessDescription':
        return fieldSchema?.max(FieldMaxLength.LONG, t('formFields.errors.fieldTooLong'))
      case 'externalReference':
        return getYupField({ label })
      case 'riskScore':
        return getYupField({ label })
      case 'eddCompleted':
        return yupBoolean().nullable()
      default:
        return fieldSchema
    }
  }

  const formSchema = yupObject(
    formKeys?.reduce((acc, key) => {
      acc[key] = getFieldSchema(key)
      return acc
    }, {} as BusinessCustomerFormSchema)
  )
  const form = useForm<BusinessCustomerFormData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const { handleSubmit, setValue, setError, control } = form
  const formValues = useWatch({ control })
  const hasErrors = !!Object.keys(form?.formState?.errors)?.length

  const { setInputField, setComboboxField, setBaseField } = useFormFields<BusinessCustomerFormKey>(
    formSchema,
    form,
    formValues
  )

  const getFormField = (key: BusinessCustomerFormKey) => {
    if (Object.keys(comboboxItems)?.includes(key)) {
      const comboboxField: ComboboxProps = {
        ...(setComboboxField({ key, items: comboboxItems?.[key] }) as ComboboxProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: defaultValues?.[key] as string,
      }
      return comboboxField
    }

    if (Object.keys(comboboxGroupItems)?.includes(key)) {
      const comboboxGroupField: ComboboxGroupProps = {
        ...(setComboboxField({ key, items: comboboxGroupItems?.[key] }) as ComboboxGroupProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: defaultValues?.[key] as string,
      }
      return comboboxGroupField
    }

    if (enableRelianceBusinessFields && key === 'eddCompleted') {
      const clearEddCompleted = () =>
        setValue('eddCompleted', undefined, { shouldValidate: true, shouldDirty: true })
      const setEddCompleted = (newVal: boolean) =>
        setValue('eddCompleted', newVal, { shouldValidate: true, shouldDirty: true })
      const handleEddTrue = () =>
        formValues?.eddCompleted === true ? clearEddCompleted() : setEddCompleted(true)
      const handleEddFalse = () =>
        formValues?.eddCompleted === false ? clearEddCompleted() : setEddCompleted(false)

      return {
        ...setBaseField({ key }),
        options: RADIO_OPTIONS,
        defaultValue: undefined,
        value: formValues?.eddCompleted,
        onChange: (incoming: boolean) => (incoming ? handleEddTrue() : handleEddFalse()),
      }
    }
    return {
      ...setInputField({ key }),
      onChange: event => setValue(key, event?.target?.value, { shouldValidate: true }),
    }
  }

  const formFields = formKeys?.reduce(
    (acc, key) => {
      acc[key] = getFormField(key)
      return acc
    },
    {} as Record<BusinessCustomerFormKey, any>
  )

  const getComboboxLabel = <LabelType extends string>(key: BusinessCustomerFormKey) => {
    return comboboxItems?.[key]?.find(({ value }) => value === formValues?.[key])
      ?.leftText as LabelType
  }

  const displayData: Partial<BusinessCustomerFormData> = {
    industry: getComboboxLabel('industry'),
    countryOfIncorporation: getComboboxLabel('countryOfIncorporation'),
    monthlyExpectedVolumes: getComboboxLabel('monthlyExpectedVolumes'),
    useCase: getComboboxLabel<UseCase>('useCase'),
    riskScore: getComboboxLabel<RiskScore>('riskScore'),
  }

  useEffect(() => {
    if (fieldErrors) {
      setFieldErrors(fieldErrors, mapBusinessErrorKeyToFormKey, setError)
    }

    if (optionErrorFields?.length) {
      optionErrorFields?.forEach(key => {
        setError(key as BusinessCustomerFormKey, {
          type: 'disabled',
          message: t('formFields.errors.loadingOptions'),
        })
      })
    }
  }, [optionErrorFields?.length])

  return { formFields, displayData, hasErrors, handleSubmit, form }
}
